import FormInput from "../FormInput";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { userAuthAPI } from "../../api/userAuthAPI";
import { toast } from "react-toastify";
import useUserStore from "../../zustand/UseUserStore";
import { SESSION_ID } from "../../api/apiConfig";
import { showSuccessToast } from "../../js/utils/toast";
import { showErrorToast } from "../../js/utils/errors";
import { useCookieConsent } from "../../context/CookieConsentContext";
import { usePhoneVerification } from "../../hooks/usePhoneVerification";

const imagePath = import.meta.env.PROD
	? import.meta.env.VITE_PROD_IMAGE_PATH
	: import.meta.env.VITE_DEV_IMAGE_PATH;
const clock = `${imagePath}/icon/clock.svg`;
const paperPlane = `${imagePath}/icon/paperPlane.svg`;

const Mobile2FA = ({
	onSubmit = () => {},
	control,
	newUser,
	creatureID,
	...otherProps
}) => {
	const codeInputRef = useRef(null);
	const navigate = useNavigate();
	// Get cookie consent - will throw if provider is not available, but it should be in App.jsx
	// If onShowCookieConsent is undefined, that's fine - we check before calling it
	const cookieConsent = useCookieConsent();
	const onShowCookieConsent = cookieConsent?.onShowCookieConsent;

	const state = useUserStore();

	// Use shared phone verification hook
	const {
		phone,
		code,
		loading,
		error,
		timer,
		codeInputKey,
		setPhone,
		setCode,
		setError,
		handleSendCode: handleSendCodeFromHook,
		handleVerifyCode: handleVerifyCodeFromHook,
		clearCode,
		cleanPhoneNumber,
		cleanedPhone,
	} = usePhoneVerification({
		creatureID: creatureID || state.user?.CreatureID || null,
		onSuccess: async (response) => {
			// CRITICAL: Check if phone number already has an account (memberID in response)
			// This handles the case where user enters existing phone during signup
			// If memberID exists, log them in and redirect appropriately
			if (response?.result?.memberID) {
				await handleGetAccount(response.result.memberID);
			} else if (newUser) {
				// New user (no existing account) - proceed with signup
				showSuccessToast("Phone verified successfully!");
				onSubmit();
			} else {
				// Fallback: if no memberID but verification succeeded, just proceed
				showSuccessToast("Phone verified successfully!");
				onSubmit();
			}
		},
		onError: (error) => {
			console.log(error);
		},
		autoStartTimer: true,
	});

	// Handles phone number submission with cookie consent check
	async function handleSendPhone() {
		// Check cookie consent first
		if (!localStorage.getItem("cookiesaccepted")) {
			setError("Cookies not accepted.");
			if (onShowCookieConsent) {
				onShowCookieConsent(); // Show the cookie consent component
			} else {
				console.warn("onShowCookieConsent is not available");
			}
			toast.error("Please accept cookies to sign in or sign up.");
			return;
		}

		// Use the shared hook's handleSendCode
		await handleSendCodeFromHook();
	}

	// Handles code submission - wrapper to match existing signature
	async function handleVerifyPhone(codeToVerify, phoneToVerify) {
		// Use the shared hook's handleVerifyCode
		await handleVerifyCodeFromHook();
	}

	// Handles account retrieval
	const handleGetAccount = async (userID) => {
		try {
			// Send to API
			const response = await userAuthAPI.getaccount(userID);
			state.setUserWithRoles(
				response.result.member,
				response.result.user_roles
			);
			if (response.result.avatar) {
				state.buildCharacter(response.result.avatar);
			}

			// Use the reusable method from the store
			const userProgress = state.setupUserProgressFromAccount(response);

			// CRITICAL: If user has completed onboarding (onboardingStep >= 3), redirect to profile
			// Otherwise, continue with signup flow to the step they haven't completed
			if (userProgress.onboardingStep >= 3) {
				showSuccessToast("Sign in successful!");
				// Redirect to profile if user is fully signed up
				navigate("/profile");
			} else {
				showSuccessToast("Phone verification successful!");
				// Continue with signup flow - onSubmit will navigate to appropriate step
				onSubmit();
			}
		} catch (error) {
			console.error("Error getting avatar:", error);
			showErrorToast(`Failed to get avatar.${error}`);
		}
	};

	// cleanPhoneNumber and timer management are now handled by usePhoneVerification hook

	const getTimerPrompt = () => {
		// Show code sent prompt for first 60-5=5 seconds
		if (timer > 60) {
			return (
				<p className="my-4 rounded-md border border-emerald-300 bg-emerald-100/90 p-2 text-center text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100">
					Code sent to {cleanedPhone.current}!
				</p>
			);
		} else if (timer > 0) {
			// Show timer from 60 to 1
			return (
				<p className="mx-auto mt-4 flex max-w-max items-center border-y border-blue-400 px-2 py-1 text-gray-700 dark:border-blue-500 dark:text-gray-200">
					<img src={clock} alt="Clock icon" className="mr-2 h-5 w-5" /> Retry in{" "}
					{timer} seconds.
				</p>
			);
		}
		// Show resend button when timer reaches 0
		return (
			<Button
				type="button"
				variant="outline"
				className="border-2 border-blue-400 text-blue-500 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-900/30"
				onClick={async () => {
					if (loading) {
						return;
					}
					// Clear code field immediately before sending new code
					clearCode();
					setError("");
					// Use prop handler if it exists, otherwise use internal one
					if (otherProps.handleSendPhone) {
						await otherProps.handleSendPhone(
							cleanedPhone.current.replace(/\D/g, "") ||
								phone.replace(/\D/g, "")
						);
						// Ensure code is cleared even if prop handler is used
						clearCode();
					} else {
						await handleSendPhone();
					}
				}}
				disabled={loading}
			>
				<img src={paperPlane} alt="Paper plane icon" className="h-5 w-5" />
				Send another code
			</Button>
		);
	};

	// Helper function to check if device is desktop (not touch device)
	const isDesktop = () => {
		return (
			window.innerWidth >= 768 &&
			!("ontouchstart" in window || navigator.maxTouchPoints > 0)
		);
	};

	// Helper function to handle Enter key press on desktop
	const handleEnterKey = (e, callback) => {
		if (e.key === "Enter" && !e.shiftKey && isDesktop()) {
			e.preventDefault();
			callback();
		}
	};

	// Use prop handleSendPhone if provided, otherwise use internal one
	const sendPhoneHandler = otherProps.handleSendPhone || handleSendPhone;

	const PhoneInput = (
		<div className="flex gap-2">
			{/* Phone Input */}
			<Input
				type="tel"
				id="phone"
				name="phone"
				placeholder="Enter phone number"
				value={phone}
				inputMode="numeric"
				pattern="[0-9]*"
				className="rounded-xl border-2 border-gray-400 py-4 text-[16px] focus:border-blue-500 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-blue-400 sm:py-6 sm:text-lg"
				style={{ fontSize: "16px" }}
				onKeyPress={(e) => {
					// Only allow numbers, backspace, delete, tab, escape, enter, and arrow keys
					const char = String.fromCharCode(e.which || e.keyCode);
					if (
						!/[0-9]/.test(char) &&
						![
							"Backspace",
							"Delete",
							"Tab",
							"Escape",
							"Enter",
							"ArrowLeft",
							"ArrowRight",
							"ArrowUp",
							"ArrowDown",
						].includes(e.key)
					) {
						e.preventDefault();
					}
				}}
				onBlur={(e) => {
					setPhone(cleanedPhone.current);
				}}
				onChange={(e) => {
					// Filter out any non-numeric characters that might have been pasted
					const numericOnly = e.target.value.replace(/[^\d-]/g, "");
					const cleanedValue = cleanPhoneNumber(numericOnly, true);
					cleanedPhone.current = cleanedValue;
					setPhone(cleanedValue);
					setError("");
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.shiftKey) {
						e.preventDefault();
						if (loading) {
							return;
						}
						let phoneDigits = (cleanedPhone.current || phone).replace(
							/\D/g,
							""
						);
						if (phoneDigits.length === 11 && phoneDigits.startsWith("1")) {
							phoneDigits = phoneDigits.substring(1);
						}
						const isValid =
							phoneDigits.length === 10 && /^[2-9]/.test(phoneDigits);
						if (!isValid) {
							setError("Enter a valid phone number");
							return;
						}
						if (otherProps.handleSendPhone) {
							otherProps.handleSendPhone(phoneDigits);
						} else {
							handleSendPhone();
						}
					}
				}}
			/>

			{/* Send Code Button */}
			{cleanedPhone.current.replace(/\D/g, "").length >= 10 && (
				<Button
					type="button"
					onClick={() => {
						if (loading) {
							return;
						}
						let phoneDigits = (cleanedPhone.current || phone).replace(
							/\D/g,
							""
						);
						if (phoneDigits.length === 11 && phoneDigits.startsWith("1")) {
							phoneDigits = phoneDigits.substring(1);
						}
						const isValid =
							phoneDigits.length === 10 && /^[2-9]/.test(phoneDigits);
						if (!isValid) {
							setError("Enter a valid phone number");
							return;
						}
						if (otherProps.handleSendPhone) {
							otherProps.handleSendPhone(phoneDigits);
						} else {
							handleSendPhone();
						}
					}}
					disabled={loading}
					className="rounded-xl bg-blue-500 px-4 py-4 text-base text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-6 sm:text-lg"
				>
					{loading ? "Sending..." : "Send Code"}
				</Button>
			)}
		</div>
	);

	const CodeInput = (
		<div className="flex gap-2">
			{/* Code Input */}
			<Input
				key={codeInputKey}
				ref={codeInputRef}
				name="code"
				inputMode="numeric"
				pattern="[0-9]*"
				placeholder="Enter verification code"
				value={code}
				className="rounded-xl border-2 border-gray-400 py-4 text-[16px] focus:border-blue-500 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-blue-400 sm:py-6 sm:text-lg"
				style={{ fontSize: "16px" }}
				onChange={(e) => {
					setCode(e.target.value);
				}}
				onKeyDown={(e) => {
					// Only handle Enter if we're in code input mode (loginAttemptId exists)
					if (e.key === "Enter" && !e.shiftKey && state.loginAttemptId) {
						e.preventDefault();
						if (code.length > 0 && !loading) {
							handleVerifyPhone(code, phone);
						}
					}
				}}
			/>

			{/* Submit Button */}
			{code.length > 0 && (
				<>
					{" "}
					{loading ? (
						<Button
							type="button"
							className="rounded-xl px-4 py-4 text-base sm:px-6 sm:py-6 sm:text-lg"
							disabled
						>
							Loading...
						</Button>
					) : (
						<Button
							type="button"
							className="rounded-xl bg-blue-500 px-4 py-4 text-base text-white hover:bg-blue-600 sm:px-6 sm:py-6 sm:text-lg"
							onClick={() => {
								if (!loading) {
									handleVerifyPhone(code, phone);
								}
							}}
							disabled={loading}
						>
							Continue
						</Button>
					)}
				</>
			)}
		</div>
	);

	return (
		<div className="mx-auto flex w-full flex-col gap-2">
			{/* Input */}
			{state.loginAttemptId ? CodeInput : PhoneInput}

			{/* Code timer prompt and controls - Only show if timer started */}
			{timer !== null && getTimerPrompt()}

			{/* Error message */}
			{error ? (
				<p className="m-1 text-left text-sm text-red-600 dark:text-red-400">
					{error}
				</p>
			) : null}

			{/* Go Back button - only show when on code verification step */}
			{state.loginAttemptId ? (
				<Button
					variant="ghost"
					type="button"
					onClick={() => {
						// Clear loginAttemptId to go back to phone input
						state.setLoginAttemptId(null);
						setCode("");
						setError("");
					}}
					className="mt-2 w-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
				>
					Go Back
				</Button>
			) : null}
		</div>
	);
};

export default Mobile2FA;
